import { useState } from 'react'

import { useAsk, useConversation } from '@/shared/api/queries'
import type { Answer } from '@/shared/api/queries'
import { useTranslate } from '@/shared/i18n'
import { Alert, Badge, Button, Card, Empty, Field, Stack, Text } from '@/shared/ui'

import './chat.css'

function Citations({ answer }: { answer: Answer }) {
  const t = useTranslate()

  if (answer.citations.length === 0) {
    return null
  }

  return (
    <Stack gap="xs">
      <Text as="h3" role="eyebrow" tone="muted">
        {t('chat.sources')}
      </Text>
      <Stack as="ul" gap="xs">
        {answer.citations.map((citation) => (
          <li key={citation.source_id}>
            <Text role="caption" tone="muted">
              <a className="textlink text text--caption text--accent" href={citation.url}>
                {citation.title ?? citation.source_id}
              </a>{' '}
              · {citation.jurisdiction} · {t('chat.verified', { date: citation.last_verified_on })}
            </Text>
          </li>
        ))}
      </Stack>
    </Stack>
  )
}

export function ChatPage() {
  const t = useTranslate()
  const [question, setQuestion] = useState('')
  const [conversationId, setConversationId] = useState<string>()

  const ask = useAsk()
  const history = useConversation(conversationId)

  function send(event: React.FormEvent) {
    event.preventDefault()

    const asked = question.trim()

    if (!asked) {
      return
    }

    ask.mutate(conversationId ? { question: asked, conversationId } : { question: asked }, {
      onSuccess: (answer) => {
        setConversationId(answer.conversation_id)
        setQuestion('')
      },
    })
  }

  const messages = history.data?.messages ?? []

  return (
    <div className="chat">
      <Stack gap="md" className="chat__thread">
        <Text as="h1" role="heading-2">
          {t('chat.title')}
        </Text>

        {messages.length === 0 && !ask.isPending ? (
          <Empty icon="💬" body={t('chat.empty')} />
        ) : null}

        <Stack as="ul" gap="sm">
          {messages.map((message) => (
            <li key={message.id} className={message.role === 'user' ? 'chat__mine' : undefined}>
              <Card flat={message.role === 'user'}>
                <Stack direction="row" justify="between" gap="sm">
                  <Text as="span" role="eyebrow" tone="muted">
                    {message.role === 'user' ? t('chat.you') : t('chat.title')}
                  </Text>
                  {message.uncertain ? <Badge status="warning">{t('chat.uncertain')}</Badge> : null}
                </Stack>
                <Text role="body" measure>
                  {message.text}
                </Text>
                {message.citations.length > 0 ? (
                  <Citations answer={message as unknown as Answer} />
                ) : null}
              </Card>
            </li>
          ))}
        </Stack>

        {ask.isPending ? (
          <Text role="caption" tone="muted">
            {t('chat.thinking')}
          </Text>
        ) : null}

        {ask.error ? <Alert status="danger" title={ask.error.message} /> : null}
      </Stack>

      <form className="chat__composer" onSubmit={send}>
        <Field label={t('chat.placeholder')}>
          {(control) => (
            <input
              {...control}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" full disabled={ask.isPending || !question.trim()}>
          {t('chat.send')}
        </Button>
      </form>
    </div>
  )
}
